{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "content_gating",
  "x-cli-editable": true,
  "x-feature": true,
  "title": "Content Gating",
  "description": "When enabled, the AI agent will offer valuable content (case studies, guides, etc.) during the conversation and ask for the visitor's email to send it. This is a lead capture mechanism — the agent detects the right moment based on the scenarios you define below.",
  "type": "object",
  "x-access": "staff",
  "properties": {
    "gate_after_messages": {
      "ui:sectionTitle": "Gating Rules",
      "ui:sectionDescription": "When Rose asks visitors to identify themselves.",
      "ui:sectionIcon": "Lock",
      "type": "integer",
      "ui:description": "How many messages should the agent wait before offering content? The agent won't propose any gated content before this many exchanges, even if a trigger matches. Set higher to let the conversation warm up first.",
      "minimum": 1,
      "maximum": 50
    },
    "scenarios": {
      "type": "array",
      "ui:description": "Each row defines a scenario: when should the agent offer content, what type, and how to describe it. The agent will ONLY offer content types listed here. Add one row per trigger/content combination.",
      "maxItems": 10,
      "items": {
        "type": "object",
        "properties": {
          "trigger": {
            "type": "string",
            "ui:description": "Describe in plain language what the visitor says or asks that should make the agent offer this content. Be specific so the AI knows when to act. Example: 'Visitor mentions their industry, sector, or business type' or 'Visitor asks about pricing or costs'.",
            "maxLength": 300,
            "minLength": 1
          },
          "content_type": {
            "type": "string",
            "ui:description": "A short label for this type of content (e.g. 'case_study', 'comparison_guide', 'personalized_video'). The agent will only offer content types that appear in your scenarios — nothing else. Use the same label across scenarios that offer the same type of content.",
            "maxLength": 100,
            "minLength": 1
          },
          "offer_description": {
            "type": "string",
            "ui:description": "This is the phrase the agent will use when offering content to the visitor. It appears in the message like: '💌 Would you like me to share [this phrase]?' — so write it naturally. Example: 'a case study from a company in your industry' → agent says '💌 Would you like me to share a case study from a company in your industry?'. If left empty, the agent falls back to the content_type label (e.g. 'a case_study'), which sounds robotic.",
            "maxLength": 300,
            "minLength": 1
          },
          "direct_capture": {
            "type": "boolean",
            "ui:description": "Enable this if the visitor is explicitly asking for this content (e.g. 'Can you send me a case study?'). When enabled, the agent skips the offer step and asks for the visitor's email right away — saving one turn in the conversation. Leave disabled for triggers where the agent should first answer the question, then propose the content."
          }
        },
        "required": [
          "trigger",
          "content_type"
        ]
      }
    },
    "require_email": {
      "ui:sectionTitle": "Required Details",
      "ui:sectionDescription": "What a visitor must share to keep chatting.",
      "ui:sectionIcon": "Mail",
      "type": "boolean",
      "ui:description": "If enabled, the agent will ask for the visitor's email address before sending the content. Disable only if you want to offer content without capturing a lead."
    },
    "require_company": {
      "type": "boolean",
      "ui:description": "If enabled, the agent will also ask for the visitor's company name in addition to their email."
    }
  },
  "default": {
    "gate_after_messages": 2,
    "scenarios": [
      {
        "trigger": "Visitor mentions their industry, sector, or vertical",
        "content_type": "case_study",
        "offer_description": "a case study from a company in your industry"
      },
      {
        "trigger": "Visitor asks about ROI, business results, or metrics",
        "content_type": "case_study",
        "offer_description": "a case study with detailed ROI breakdown"
      },
      {
        "trigger": "Visitor asks about existing customers, references, or social proof",
        "content_type": "case_study",
        "offer_description": "a relevant customer case study"
      },
      {
        "trigger": "Visitor compares to competitors or alternatives",
        "content_type": "comparison_guide",
        "offer_description": "a detailed comparison document"
      },
      {
        "trigger": "Visitor shares qualifying information (company size, budget, timeline)",
        "content_type": "case_study",
        "offer_description": "a case study from a similar company"
      }
    ],
    "require_email": true,
    "require_company": false
  }
}
